import { BaseService } from '../../../../../core/services/base.service';
import { getApiBaseUrl } from '../../../../../core/config/runtimeConfig';
import {
  CandidateMessage,
  CandidateMessageThread,
  PublicCandidateMessageThread,
  isCandidateMessage,
} from '../models/candidate-message.model';

const propagateError = (error: Error): never => {
  throw error;
};

const requireMessage = (value: unknown): CandidateMessage => {
  if (!isCandidateMessage(value)) {
    throw new Error('The candidate message response was invalid.');
  }
  return value;
};

const requireThread = (value: unknown): CandidateMessageThread => {
  if (!value || typeof value !== 'object') {
    throw new Error('The candidate conversation response was invalid.');
  }

  const thread = value as Partial<CandidateMessageThread>;
  if (
    !Array.isArray(thread.messages) ||
    !thread.messages.every(isCandidateMessage)
  ) {
    throw new Error('The candidate conversation response was invalid.');
  }
  return thread as CandidateMessageThread;
};

export class CandidateMessageService {
  static async list(applicationId: string): Promise<CandidateMessageThread> {
    const thread = await BaseService.Get(
      `/job-applications/${applicationId}/messages`,
      propagateError,
      false
    );
    return requireThread(thread);
  }

  static async send(
    applicationId: string,
    body: string
  ): Promise<CandidateMessage> {
    const sent = await BaseService.Post(
      `/job-applications/${applicationId}/messages`,
      { body },
      propagateError,
      false
    );
    return requireMessage(sent);
  }

  private static async publicRequest<T>(
    path: string,
    body?: object
  ): Promise<T> {
    const response = await fetch(`${getApiBaseUrl()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 410) throw new Error('GONE');
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.message || 'REQUEST_FAILED');
    }
    return response.json() as Promise<T>;
  }

  static publicView(token: string): Promise<PublicCandidateMessageThread> {
    return this.publicRequest(`/public/candidate-conversations/${token}`);
  }

  static reply(token: string, body: string): Promise<CandidateMessage> {
    return this.publicRequest(
      `/public/candidate-conversations/${token}/messages`,
      {
        body,
      }
    );
  }
}
